import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Paper,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { formatDateField } from "../../../../../../base/FormatTime";
import {
  cornellDepressionLevels,
  fields,
} from "./cornellDepressionScale.config";

// A styled container for each section of the modal
const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

// A styled component for displaying a single key-value pair
const DetailItem = ({ label, value, isLast = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 1.5,
      borderBottom: !isLast ? 1 : 0,
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, textAlign: "right" }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: "medium", color: "text.primary" }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

const getChoiceLabel = (value) => {
  switch (value) {
    case 0:
      return "Absent";
    case 1:
      return "Mild to intermittent";
    case 2:
      return "Severe";
    case null:
    default:
      return "Unable to evaluate";
  }
};

const CornellDepressionScaleDetailsModal = ({
  open,
  handleClose,
  assessment,
}) => {
  if (!assessment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Cornell Depression Scale Details
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "grey.50", p: 3 }}>
        <Stack spacing={2}>
          <Section title="General Information">
            <DetailItem label="Service User" value={assessment.client_name} />
            <DetailItem
              label="Date of Assessment"
              value={formatDateField(assessment.created_at)}
            />
            <DetailItem label="Created By" value={assessment.created_by_name} />
            <DetailItem label="Care Plan" value={assessment.care_plan_name} />
            <DetailItem
              label="Total Score"
              isLast
              value={`${assessment.score} / 38`}
            />
          </Section>

          <Section title="Assessment Details">
            {fields.map((field, index) => (
              <DetailItem
                key={field.name}
                label={field.label}
                value={getChoiceLabel(assessment[field.name])}
                isLast={index === fields.length - 1}
              />
            ))}
          </Section>

          <Section title="Comments">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
            >
              {assessment.comments || "No comments provided."}
            </Typography>
          </Section>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CornellDepressionScaleDetailsModal;
